import React, { useState } from 'react';
import { ArrowRight, Phone, Mail, MapPin, CircleCheck, Building2, Hotel, Briefcase, HeartPulse, PartyPopper, ShoppingBag, GraduationCap, Map, BarChart3, TrendingUp, Handshake } from 'lucide-react';
import { useScrollReveal } from '../hooks/useAnimations';

const Reveal: React.FC<{ children: React.ReactNode; className?: string; delay?: number }> = ({ children, className = '', delay = 0 }) => {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();
  return (
    <div ref={ref} className={`transition-all duration-[800ms] ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

const COOLDOWN_SECONDS = 60;

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', company: '', message: '', type: 'general' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const loadedAt = React.useRef(Date.now());

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  React.useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Timing check: bots submit instantly
    if (Date.now() - loadedAt.current < 3000) {
      setError('Submission too fast. Please try again.');
      return;
    }
    // Rate limiting
    if (cooldown > 0) {
      setError(`Please wait ${cooldown}s before submitting again.`);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: process.env.REACT_APP_WEB3FORM_ACCESS_KEY,
          subject: `Contact Form – ${formData.type} inquiry from ${formData.name}`,
          botcheck: false,
          ...formData,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSubmitted(true);
        setFormData({ name: '', email: '', phone: '', company: '', message: '', type: 'general' });
        setCooldown(COOLDOWN_SECONDS);
        loadedAt.current = Date.now();
      } else {
        setError(data.message || 'Something went wrong. Please try again.');
      }
    } catch {
      setError('Failed to send message. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="bg-white overflow-hidden">

      {/* ═══ HERO ═══ */}
      <section className="relative pt-28 pb-14 lg:pt-36 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-silver-50 via-brand-50/30 to-white" />
        <div className="absolute inset-0 dot-grid opacity-[0.3]" />
        <div className="absolute top-20 left-[15%] w-[450px] h-[450px] bg-brand-200/20 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 right-[10%] w-[350px] h-[350px] bg-violet-200/15 rounded-full blur-[100px] pointer-events-none" />

        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <span className="inline-block text-[11px] font-bold uppercase tracking-[0.2em] text-brand-600 bg-brand-50 px-4 py-1.5 rounded-full mb-5 animate-fade-in">Contact</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-silver-950 tracking-tight leading-[1.1] animate-slide-up">
            Let's Start a{' '}
            <span className="text-gradient">Conversation</span>
          </h1>
          <p className="mt-7 text-lg text-silver-500 max-w-2xl mx-auto leading-relaxed animate-slide-up-delayed">
            Whether you're looking for bulk orders, distribution partnerships, or custom branding — our team is ready to help.
          </p>
        </div>
      </section>

      {/* ═══ FORM + CONTACT INFO ═══ */}
      <section className="py-12 lg:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">

            {/* ── FORM ── */}
            <Reveal className="lg:col-span-3">
              <div className="bg-white border border-silver-100 rounded-2xl p-8 lg:p-10 shadow-card">
                <h2 className="text-2xl font-bold text-silver-900 mb-2">Send us a message</h2>
                <p className="text-sm text-silver-500 mb-8">Fill out the form and we'll get back to you within 24 hours.</p>

                {submitted ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mb-5">
                      <CircleCheck className="w-8 h-8 text-emerald-500" />
                    </div>
                    <h3 className="text-xl font-bold text-silver-900">Message Sent!</h3>
                    <p className="text-sm text-silver-500 mt-2">Our team will reach out to you shortly.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Honeypot – hidden from real users; bots fill this in and Web3Forms rejects the submission */}
                    <input type="checkbox" name="botcheck" className="hidden" style={{ display: 'none' }} aria-hidden="true" tabIndex={-1} />
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-semibold text-silver-700 uppercase tracking-wider mb-2">Full Name *</label>
                        <input
                          type="text"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl border border-silver-200 bg-silver-50/50 text-silver-900 text-sm placeholder:text-silver-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400 transition-all duration-200"
                          placeholder="Your name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-silver-700 uppercase tracking-wider mb-2">Email *</label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl border border-silver-200 bg-silver-50/50 text-silver-900 text-sm placeholder:text-silver-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400 transition-all duration-200"
                          placeholder="you@company.com"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-semibold text-silver-700 uppercase tracking-wider mb-2">Phone</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl border border-silver-200 bg-silver-50/50 text-silver-900 text-sm placeholder:text-silver-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400 transition-all duration-200"
                          placeholder="+91 XXXXX XXXXX"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-silver-700 uppercase tracking-wider mb-2">Inquiry Type</label>
                        <select
                          name="type"
                          value={formData.type}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl border border-silver-200 bg-silver-50/50 text-silver-900 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400 transition-all duration-200"
                        >
                          <option value="general">General Inquiry</option>
                          <option value="bulk">Bulk Order</option>
                          <option value="distribution">Distribution</option>
                          <option value="branding">Custom Branding</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-silver-700 uppercase tracking-wider mb-2">Company / Organization</label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-silver-200 bg-silver-50/50 text-silver-900 text-sm placeholder:text-silver-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400 transition-all duration-200"
                        placeholder="Your organization"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-silver-700 uppercase tracking-wider mb-2">Message *</label>
                      <textarea
                        name="message"
                        required
                        rows={5}
                        value={formData.message}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-silver-200 bg-silver-50/50 text-silver-900 text-sm placeholder:text-silver-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400 transition-all duration-200 resize-none"
                        placeholder="Tell us about your requirements..."
                      />
                    </div>

                    {error && (
                      <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{error}</p>
                    )}

                    <button
                      type="submit"
                      disabled={loading || cooldown > 0}
                      className="shimmer-btn group w-full sm:w-auto inline-flex items-center justify-center gap-2.5 bg-brand-600 text-white px-8 py-4 rounded-full text-sm font-bold hover:bg-brand-700 hover:shadow-xl hover:shadow-brand-200/30 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed disabled:scale-100"
                    >
                      {loading ? 'Sending…' : cooldown > 0 ? `Wait ${cooldown}s` : 'Send Message'}
                      {!loading && cooldown === 0 && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />}
                    </button>
                  </form>
                )}
              </div>
            </Reveal>

            {/* ── CONTACT INFO ── */}
            <Reveal delay={200} className="lg:col-span-2">
              <div className="space-y-6">
                {/* Quick contact cards */}
                {[
                  { icon: <Phone className="w-5 h-5 text-brand-500" />, label: 'Phone', value: '1800-233-2727', sub: 'Mon–Sat, 9AM–6PM IST', bg: 'from-brand-50 to-brand-100/50' },
                  { icon: <Mail className="w-5 h-5 text-emerald-500" />, label: 'Email', value: 'nathkrupaenergy@gmail.com', sub: 'nathkrupaenterprises27@gmail.com', bg: 'from-emerald-50 to-emerald-100/50' },
                  { icon: <MapPin className="w-5 h-5 text-violet-500" />, label: 'Office', value: 'Metro complex, Kuldharan road, Karjat', sub: 'Dist. Anagar, Maharashtra 414402', bg: 'from-violet-50 to-violet-100/50' },
                ].map((item, i) => (
                  <div key={i} className="group bg-white border border-silver-100 rounded-2xl p-6 hover:shadow-card hover:-translate-y-0.5 transition-all duration-300 gradient-border">
                    <div className="flex items-start gap-4">
                      <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${item.bg} flex items-center justify-center flex-shrink-0`}>
                        {item.icon}
                      </div>
                      <div>
                        <div className="text-[10px] font-bold text-silver-400 uppercase tracking-wider">{item.label}</div>
                        <div className="text-sm font-semibold text-silver-900 mt-0.5">{item.value}</div>
                        <div className="text-xs text-silver-500 mt-1">{item.sub}</div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Map placeholder */}
                <div className="bg-gradient-to-br from-silver-50 to-brand-50/30 border border-silver-100 rounded-2xl overflow-hidden" style={{ height: '200px' }}>
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-center">
                      <MapPin className="w-8 h-8 text-brand-300 mx-auto mb-2" />
                      <p className="text-xs text-silver-400 font-medium">Karjat, Dist. Anagar, Maharashtra</p>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>

          </div>
        </div>
      </section>

      {/* ═══ DISTRIBUTOR BENEFITS ═══ */}
      <section className="py-16 lg:py-20 bg-gradient-to-b from-silver-50/50 to-white relative">
        <div className="absolute inset-0 dot-grid opacity-[0.2]" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-16">
              <span className="inline-block text-[11px] font-bold uppercase tracking-[0.2em] text-brand-600 bg-brand-50 px-4 py-1.5 rounded-full mb-4">Partnerships</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-silver-950 tracking-tight">
                Become a <span className="text-gradient">Distributor</span>
              </h2>
              <p className="mt-5 text-silver-500 max-w-lg mx-auto text-lg">
                Join our growing network and bring premium hydration to your region.
              </p>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { title: 'Exclusive Territory', desc: 'Protected geographical zones for your distribution rights.', icon: <Map className="w-7 h-7 text-brand-500" /> },
              { title: 'Marketing Support', desc: 'Co-branded materials, POS displays, and campaign support.', icon: <BarChart3 className="w-7 h-7 text-violet-500" /> },
              { title: 'Competitive Margins', desc: 'Industry-leading margins with volume-based incentives.', icon: <TrendingUp className="w-7 h-7 text-emerald-500" /> },
              { title: 'Training & Support', desc: 'Full onboarding, product training, and dedicated support.', icon: <Handshake className="w-7 h-7 text-amber-500" /> },
            ].map((benefit, i) => (
              <Reveal key={i} delay={i * 100}>
                <div className="group bg-white border border-silver-100 rounded-2xl p-7 hover:shadow-card-hover hover:border-silver-200/80 hover:-translate-y-1 transition-all duration-500 gradient-border h-full text-center">
                  <div className="flex justify-center mb-4">{benefit.icon}</div>
                  <h3 className="text-sm font-bold text-silver-900 mb-2">{benefit.title}</h3>
                  <p className="text-xs text-silver-500 leading-relaxed">{benefit.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ B2B SECTORS ═══ */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-16">
              <span className="inline-block text-[11px] font-bold uppercase tracking-[0.2em] text-brand-600 bg-brand-50 px-4 py-1.5 rounded-full mb-4">Industries</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-silver-950 tracking-tight">
                We Serve Every <span className="text-gradient">Sector</span>
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { name: 'Hotels & Resorts', icon: <Hotel className="w-7 h-7 text-brand-500" /> },
              { name: 'Corporate Offices', icon: <Briefcase className="w-7 h-7 text-violet-500" /> },
              { name: 'Hospitals', icon: <HeartPulse className="w-7 h-7 text-emerald-500" /> },
              { name: 'Events & Weddings', icon: <PartyPopper className="w-7 h-7 text-amber-500" /> },
              { name: 'Retail & Stores', icon: <ShoppingBag className="w-7 h-7 text-sky-500" /> },
              { name: 'Education', icon: <GraduationCap className="w-7 h-7 text-rose-500" /> },
            ].map((sector, i) => (
              <Reveal key={i} delay={i * 60}>
                <div className="group bg-white border border-silver-100 rounded-2xl p-6 hover:shadow-card hover:-translate-y-0.5 transition-all duration-300 text-center gradient-border">
                  <div className="flex justify-center mb-3 group-hover:scale-110 transition-transform duration-300">{sector.icon}</div>
                  <div className="text-xs font-bold text-silver-700">{sector.name}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section className="relative py-16 lg:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-700 via-brand-600 to-brand-800" />
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-white/5 rounded-full" />
        </div>

        <div className="relative max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <Reveal>
            <div className="flex items-center justify-center gap-3 mb-6">
              <Building2 className="w-6 h-6 text-white/40" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-white/50">Nathkrupa Energy Pvt. Ltd.</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
              Premium Water. <br className="sm:hidden" />Trusted Partner.
            </h2>
            <p className="mt-6 text-lg text-brand-100/70 max-w-xl mx-auto leading-relaxed">
              Serving businesses across Maharashtra and beyond with India's finest packaged drinking water.
            </p>
          </Reveal>
        </div>
      </section>

    </main>
  );
};

export default Contact;
